import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from 'app/shared/api.service';
import { CartService } from 'app/core/cart/cart.service';

export interface ProductCategory {
    id: string;
    name: string;
    description?: string | null;
    sort_order?: number;
}

export interface ApiBundlingPackage {
    id: string;
    name: string;
    description?: string;
    regular_price: number;
    price: number;
    product_categories: ProductCategory[];
    is_active?: boolean;
    product_package_type?: string;
}

export interface PackageItem {
    id: string;
    name: string;
    subtitle: string;
    discountPercent: string;
    iconType: 'click-heart' | 'thumbs-up' | 'heart-eyes' | 'laptop-heart';
    categoryLabel: string;
    features: string[];
    moreFeatures: string[];
    bonus?: string;
    bonuses?: string[];
    showMore: boolean;
    price: number;
    priceFormatted: string;
    originalPrice: number;
    originalPriceFormatted: string;
    isFeatured?: boolean;
    productCategories?: ProductCategory[];
}

@Component({
    selector: 'app-package',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './package.component.html',
    styleUrl: './package.component.scss',
})
export class PackageComponent implements OnInit {
    isLoading: boolean = false;
    errorMessage: string | null = null;
    bundlingPackages: PackageItem[] = [];

    // Mapping of category features for rich and dynamic feature lists
    private readonly categoryFeatureMap: { [key: string]: { features: string[]; moreFeatures: string[] } } = {
        'digital invitation': {
            features: [
                'Tema Undangan Premium',
                'Waktu Dan Lokasi Acara Google Maps',
                'Amplop Digital & E-Wallet',
                'RSVP Konfirmasi Kehadiran',
                'Kisah Cinta (Love Story)',
            ],
            moreFeatures: [
                'Countdown Waktu Acara & Kalender Google',
                'Galeri Foto & Video Prewedding HD',
                'Musik Romantis Pilihan',
                'Kustom Nama Tamu Undangan Tanpa Batas',
            ]
        },
        'digital guest book': {
            features: [
                'Buku Tamu Digital (QR Code Check-in)',
                'Kustom Desain Sesuai Konsep Acara',
                'Rekapitulasi Kehadiran Real-time',
            ],
            moreFeatures: [
                'Cetak Label Nama Tamu Instan',
                'Sistem Sinkronisasi & Manajemen Souvenir',
                'Export Data Tamu Lengkap ke Excel',
            ]
        },
        'photobooth': {
            features: [
                'Photobooth Unlimited Prints',
                'Custom Frame & Desain Template',
                'Lighting Studio & Operator Standby',
            ],
            moreFeatures: [
                'Softfile Instant Download via QR Code',
                'Properti & Aksesoris Foto Lengkap',
                'Pilihan Cetak Strip & 4R Kualitas Terbaik',
            ]
        },
        'videobooth': {
            features: [
                '360 Spin Videobooth Aesthetic',
                'Custom Music Overlay & Frame Video',
                'Efek Slow-Motion & Boomerang Dinamis',
            ],
            moreFeatures: [
                'Platform 360 Kapasitas 3-4 Orang',
                'Instant QR Code Airdrop & Download',
                'Operator & Lighting Ring Studio',
            ]
        }
    };

    constructor(
        private apiService: ApiService,
        private cartService: CartService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.fetchBundlingPackages();
    }

    async fetchBundlingPackages(): Promise<void> {
        this.isLoading = true;
        this.errorMessage = null;

        try {
            const apiEndpoint = '/api/v1/front/public/bundling-packages/list?paginate=false&search=&page=&per_page=';
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data?.list) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Fallback fetch error:', e);
                }
            }

            if (response && response.data && Array.isArray(response.data.list) && response.data.list.length > 0) {
                this.bundlingPackages = this.transformApiPackages(response.data.list);
            } else {
                this.bundlingPackages = [];
            }
        } catch (error) {
            console.error('Error fetching bundling packages:', error);
            this.bundlingPackages = [];
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    private transformApiPackages(apiList: ApiBundlingPackage[]): PackageItem[] {
        const iconList: ('click-heart' | 'thumbs-up' | 'heart-eyes' | 'laptop-heart')[] = [
            'click-heart',
            'thumbs-up',
            'heart-eyes',
            'laptop-heart'
        ];

        return apiList.map((item, index) => {
            const categories = item.product_categories || [];
            
            // Format Title Case for Name
            const nameFormatted = this.toTitleCase(item.name || 'Paket Spesial');

            // Format Subtitle from categories
            const categoryNames = categories.map(c => this.formatCategoryName(c.name));
            const subtitle = categoryNames.length > 0 ? `( ${categoryNames.join(' + ')} )` : '( Paket Bundling Lengkap )';

            // Calculate Prices & Discounts
            const originalPrice = item.regular_price || 0;
            
            // In API, if price is small like 3 or 6 (meaning multiplier/ratio) or normal price
            let price = item.price || 0;
            if (price > 0 && price <= 1000 && originalPrice > 1000) {
                // If price in API is e.g. 3, 6, 9 -> calculate 10% discount from regular_price or price in millions
                price = Math.round(originalPrice * 0.9);
            } else if (price === 0 && originalPrice > 0) {
                price = Math.round(originalPrice * 0.9);
            }

            let discountPercent = '10%';
            if (originalPrice > price && originalPrice > 0) {
                const calculatedPercent = Math.round(((originalPrice - price) / originalPrice) * 100);
                if (calculatedPercent > 0) {
                    discountPercent = `${calculatedPercent}%`;
                }
            }

            // Build features dynamically based on product categories
            const { features, moreFeatures } = this.extractFeaturesFromCategories(categories);

            // Icon assignment
            const iconType = iconList[index % iconList.length];

            // Determine if featured (e.g. exclusive or 3rd package or with 4 categories)
            const isFeatured = item.name.toLowerCase().includes('exclusive') || index === 2 || categories.length >= 4;

            // Bonus assignment
            let bonus = '-';
            if (item.name.toLowerCase().includes('exclusive') || categories.length >= 3) {
                bonus = 'App Buku Tamu';
            }

            return {
                id: item.id,
                name: nameFormatted,
                subtitle: subtitle,
                discountPercent: discountPercent,
                iconType: iconType,
                categoryLabel: 'Undangan',
                features: features,
                moreFeatures: moreFeatures,
                bonus: bonus,
                bonuses: [bonus],
                showMore: false,
                price: price,
                priceFormatted: this.formatCurrency(price),
                originalPrice: originalPrice,
                originalPriceFormatted: this.formatCurrency(originalPrice),
                isFeatured: isFeatured,
                productCategories: categories
            };
        });
    }

    private extractFeaturesFromCategories(categories: ProductCategory[]): { features: string[]; moreFeatures: string[] } {
        const allFeatures: string[] = [];
        const allMoreFeatures: string[] = [];

        categories.forEach(cat => {
            const key = (cat.name || '').toLowerCase().trim();
            const matched = Object.keys(this.categoryFeatureMap).find(k => key.includes(k));
            
            if (matched && this.categoryFeatureMap[matched]) {
                const config = this.categoryFeatureMap[matched];
                allFeatures.push(...config.features);
                allMoreFeatures.push(...config.moreFeatures);
            } else {
                allFeatures.push(`Fitur Lengkap ${this.toTitleCase(cat.name)}`);
            }
        });

        // Limit top visible features to 5, and rest go to moreFeatures
        let visibleFeatures = allFeatures.slice(0, 5);
        let extraFeatures = [...allFeatures.slice(5), ...allMoreFeatures];

        // Remove duplicates
        visibleFeatures = Array.from(new Set(visibleFeatures));
        extraFeatures = Array.from(new Set(extraFeatures)).filter(f => !visibleFeatures.includes(f));

        return {
            features: visibleFeatures,
            moreFeatures: extraFeatures
        };
    }

    private formatCategoryName(name: string): string {
        const lower = (name || '').toLowerCase().trim();
        if (lower.includes('digital invitation') || lower.includes('invitation')) return 'Undangan';
        if (lower.includes('digital guest book') || lower.includes('guest book')) return 'Buku Tamu';
        if (lower.includes('photobooth')) return 'Photobooth';
        if (lower.includes('videobooth')) return 'Videobooth';
        return this.toTitleCase(name);
    }

    private toTitleCase(str: string): string {
        if (!str) return '';
        return str
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    formatCurrency(val: number): string {
        if (!val && val !== 0) return 'Rp0';
        return 'Rp' + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }

    toggleShowMore(pkg: PackageItem, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        const willExpand = !pkg.showMore;
        if (willExpand && this.bundlingPackages) {
            this.bundlingPackages.forEach(p => {
                if (p !== pkg) {
                    p.showMore = false;
                }
            });
        }
        pkg.showMore = willExpand;
        this.cdr.markForCheck();
    }

    orderPackage(pkg: PackageItem): void {
        const isUuid = this.cartService.isUuid(pkg.id);
        this.cartService.addItem({
            id: isUuid ? pkg.id : `bundling_${pkg.id}`,
            itemable_type: 'BundlingPackage',
            itemable_id: isUuid ? pkg.id : undefined,
            name: pkg.name,
            category: 'Bundling Package',
            price: pkg.price,
            priceFormatted: pkg.priceFormatted,
            image: '/assets/visidea/banner_slide.jpg',
            description: pkg.subtitle,
            selectedOption: `Paket: ${pkg.name}`,
        }, 1);
        this.cartService.openCart();
    }
}
